import React, { useState, useEffect } from "react";
import {
    Card, CardBody, Button, Spinner, Container, Badge,
    Modal, Row, Col, InputGroup, Input, FormGroup, Label,
    ModalHeader, ModalBody, ModalFooter, Nav, NavItem, NavLink, TabContent, TabPane
} from "reactstrap";
import DataTable from "react-data-table-component";
import Breadcrumbs from "../../CommonElements/Breadcrumbs";
import { apiPost, apiGet, mediaUrl } from "../../Utils";
import { ToastContainer, toast } from "react-toastify";
import { useConfig } from "../../Context/ConfigContext";
import "react-toastify/dist/ReactToastify.css";
import { H3, P } from "../../AbstractElements";
import {
    Eye, User, Calendar, MapPin, Edit,
    CheckCircle, XCircle, Clock, Truck,
    RefreshCcw, Save, Trash2, Star, Phone, Info, Shield
} from 'react-feather';
import LocationHistoryMap from './LocationHistoryMap';

interface BookingData {
    id: number;
    patient_name: string;
    patient_mobile: string;
    date: string;
    status: string;
    booking_type: string;
    service_location: string;
    address: string;
    slot_start_time: string;
    slot_end_time: string;
    created_at: string;
    latitude?: number;
    longitude?: number;
    patient_age?: number;
    patient_dob?: string;
    bystander_id?: number;
    start_code?: string;
    end_code?: string;
    actual_start_at?: string;
    actual_end_at?: string;
    total_amount?: string;
    payment_status?: string;
    is_modified?: boolean;
    modified_at?: string;
    modification_reason?: string;
    reassign_reason?: string;
    rejection_detail?: string;
    reassign_attempt_count?: number;
    last_reassign_attempt_at?: string;
    notified_bystander_ids?: number[] | string | null;
    original_bystander_id?: number;
    bystander?: {
        id: number;
        name: string;
        phone_number: string;
    };
    original_bystander?: {
        id: number;
        name: string;
        phone_number: string;
    };
    user?: {
        name: string;
        phone_number: string;
    };
    review?: {
        rating: number;
        comment: string;
        created_at: string;
    };
}

interface BystanderInfo {
    id: number;
    name: string;
    phone_number: string;
}

export default function ViewBookings() {
    const [data, setData] = useState<BookingData[]>([]);
    const [loading, setLoading] = useState(true);
    const [totalRows, setTotalRows] = useState(0);
    const [perPage, setPerPage] = useState(10);
    const [currentPage, setCurrentPage] = useState(1);
    const [sortBy, setSortBy] = useState("id");
    const [sortDir, setSortDir] = useState("desc");
    const [refresh, setRefresh] = useState(0);
    const [searchTerm, setSearchTerm] = useState("");
    const [selectedStatus, setSelectedStatus] = useState("");

    // Modal states
    const [viewModal, setViewModal] = useState(false);
    const [editModal, setEditModal] = useState(false);
    const [selectedBooking, setSelectedBooking] = useState<BookingData | null>(null);

    // Update states
    const [bystanders, setBystanders] = useState<BystanderInfo[]>([]);
    const [updateLoading, setUpdateLoading] = useState(false);
    const [editStatus, setEditStatus] = useState("");
    const [editBystanderId, setEditBystanderId] = useState<number | string>("");
    const [activeTab, setActiveTab] = useState('1');
    const [belongings, setBelongings] = useState<any[]>([]);
    const [loadingBelongings, setLoadingBelongings] = useState(false);
    const { config } = useConfig();

    const fetchBelongings = async (bookingId: number) => {
        setLoadingBelongings(true);
        try {
            const res = await apiGet(`booking/${bookingId}/belongings`);
            if (res && res.status) {
                setBelongings(res.belongings || []);
            }
        } catch (e) {
            console.error(e);
        } finally {
            setLoadingBelongings(false);
        }
    };

    const toggleTab = (tab: string) => {
        if (activeTab !== tab) {
            setActiveTab(tab);
            if (tab === '4' && selectedBooking) {
                fetchBelongings(selectedBooking.id);
            }
        }
    };

    const getBelongingPhotoUrl = (b: any) => {
        if (!b) return '';
        if (b.photo_full_url) {
            if (b.photo_full_url.includes('/storage/') && !b.photo_full_url.includes('/storage/uploads/')) {
                return b.photo_full_url.replace('/storage/', '/storage/uploads/');
            }
            return b.photo_full_url;
        }
        if (!b.photo_url) return '';
        if (b.photo_url.startsWith('http')) return b.photo_url;
        const clean = b.photo_url.replace(/^\/+/, '');
        const base = mediaUrl.endsWith('/') ? mediaUrl : `${mediaUrl}/`;
        return clean.startsWith('uploads/') ? `${base.replace(/\/uploads\/$/, '/')}${clean}` : `${base}${clean}`;
    };

    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm, selectedStatus]);

    useEffect(() => {
        const delayDebounceFn = setTimeout(() => {
            fetchData(currentPage, perPage, sortBy, sortDir, searchTerm, selectedStatus);
        }, 500);
        return () => clearTimeout(delayDebounceFn);
    }, [searchTerm, selectedStatus, refresh, currentPage, perPage, sortBy, sortDir]);

    useEffect(() => {
        if (editModal || viewModal) {
            fetchBystanders();
        }
    }, [editModal, viewModal]);

    const fetchData = async (page: number, size: number, sort: string, direction: string, search: string, status: string) => {
        setLoading(true);
        try {
            const response: any = await apiPost(`booking/get_all?page=${page}`, {
                per_page: size,
                sort_by: sort,
                sort_direction: direction,
                search: search,
                status: status
            });
            setData(response.data || []);
            setTotalRows(response.total || 0);
        } catch (error) {
            // toast.error("Failed to load bookings");
        } finally {
            setLoading(false);
        }
    };

    const fetchBystanders = async () => {
        try {
            const response: any = await apiPost('bystander/get_all', { per_page: 100 });
            setBystanders(response.data || []);
        } catch (error) {
            console.error("Failed to load bystanders");
        }
    };

    const handlePageChange = (page: number) => setCurrentPage(page);
    const handlePerRowsChange = async (newPerPage: number, page: number) => {
        setPerPage(newPerPage);
        setCurrentPage(page);
    };

    const handleSort = (column: any, sortDirection: string) => {
        setSortBy(column.sortField || column.selector?.toString().split('.').pop() || "id");
        setSortDir(sortDirection);
    };

    const handleView = (booking: BookingData) => {
        setSelectedBooking(booking);
        setActiveTab('1');
        setViewModal(true);
        fetchBelongings(booking.id);
    };

    const handleEditInitiate = (booking: BookingData) => {
        setSelectedBooking(booking);
        setEditStatus(booking.status);
        setEditBystanderId(booking.bystander?.id || "");
        setEditModal(true);
    };

    const handleUpdateBooking = async () => {
        if (!selectedBooking) return;

        setUpdateLoading(true);
        try {
            const response: any = await apiPost('booking/update', {
                booking_id: selectedBooking.id,
                status: editStatus,
                bystander_id: editBystanderId
            });

            if (response.status) {
                toast.success("Booking updated successfully");
                setEditModal(false);
                setRefresh(prev => prev + 1);
            } else {
                toast.error(response.message || "Update failed");
            }
        } catch (error) {
            toast.error("An error occurred while updating");
        } finally {
            setUpdateLoading(false);
        }
    };

    const getStatusConfig = (status: string) => {
        switch (status) {
            case 'completed': return { color: 'success', icon: <CheckCircle size={14} />, label: 'Completed' };
            case 'pending': return { color: 'warning', icon: <Clock size={14} />, label: 'Pending' };
            case 'active': return { color: 'primary', icon: <Truck size={14} />, label: 'Active' };
            case 'accepted': return { color: 'info', icon: <Info size={14} />, label: 'Accepted' };
            case 'cancelled': return { color: 'danger', icon: <XCircle size={14} />, label: 'Cancelled' };
            case 'expired': return { color: 'secondary', icon: <Clock size={14} />, label: 'Expired' };
            case 'rejected': return { color: 'danger', icon: <XCircle size={14} />, label: 'Rejected' };
            case 'reassigning': return { color: 'info', icon: <RefreshCcw size={14} />, label: 'Reassigning' };
            default: return { color: 'light', icon: null, label: status };
        }
    };

    const getStatusBadge = (status: string) => {
        const config = getStatusConfig(status);
        return (
            <Badge color={config.color} className="d-flex align-items-center gap-1 py-1 px-2 rounded-pill shadow-sm">
                {config.icon}
                <span>{config.label}</span>
            </Badge>
        );
    };

    const columns: any[] = [
        {
            name: <span className="text-muted small text-uppercase fw-bold">Sl.No</span>,
            selector: (row: BookingData, index: number) => (currentPage - 1) * perPage + index + 1,
            width: "70px"
        },
        {
            name: <span className="text-muted small text-uppercase fw-bold">Patient</span>,
            selector: (row: BookingData) => row.patient_name,
            sortable: true,
            sortField: "patient_name",
            cell: (row: BookingData) => (
                <div className="py-2">
                    <div className="fw-bold text-dark">{row.patient_name}</div>
                    <small className="text-muted d-block">{row.patient_mobile}</small>
                    <div className="mt-1 pt-1 border-top" style={{ borderStyle: 'dotted', borderColor: '#e5e7eb' }}>
                        <span className="text-primary fw-bold" style={{ fontSize: '10px' }}>BOOKED BY: </span>
                        <span className="text-dark fw-bold" style={{ fontSize: '10px' }}>{row.user?.name || "N/A"}</span>
                    </div>
                </div>
            )
        },
        {
            name: <span className="text-muted small text-uppercase fw-bold">Schedule</span>,
            selector: (row: BookingData) => row.date,
            sortable: true,
            sortField: "date",
            cell: (row: BookingData) => (
                <div className="py-2">
                    <div className="d-flex align-items-center gap-1 small fw-bold text-dark">
                        <Calendar size={12} className="text-muted" /> {(() => {
                            const d = new Date(row.date);
                            const day = d.getDate();
                            const month = d.getMonth() + 1;
                            const year = d.getFullYear().toString().slice(-2);
                            return `${day}/${month}/${year}`;
                        })()}
                    </div>
                    <div className="d-flex align-items-center gap-2 mt-1">
                        <Badge color={row.booking_type === 'booknow' ? 'danger' : 'success'} pill className="px-2 py-1 text-uppercase" style={{ fontSize: '9px', fontWeight: '800' }}>
                            {row.booking_type === 'booknow' ? 'Instant' : 'Planned'}
                        </Badge>
                        <span className="small text-muted fw-medium d-flex align-items-center gap-1">
                            <MapPin size={10} /> {row.service_location}
                        </span>
                    </div>
                    <div className="small text-muted mt-1 fw-medium">
                        <Clock size={10} /> {row.slot_start_time.substring(0, 5)} - {row.slot_end_time.substring(0, 5)}
                    </div>
                </div>
            )
        },
        {
            name: <span className="text-muted small text-uppercase fw-bold">Bystander</span>,
            selector: (row: BookingData) => row.bystander?.name || 'Unassigned',
            sortable: false,
            cell: (row: BookingData) => (
                <div className="py-2">
                    {row.bystander ? (
                        <>
                            <div className="fw-semibold text-dark d-flex align-items-center gap-1">
                                <User size={12} className="text-info" /> {row.bystander.name}
                            </div>
                            <small className="text-muted">{row.bystander.phone_number}</small>
                        </>
                    ) : (
                        <span className="text-muted italic small">Not Assigned</span>
                    )}
                </div>
            )
        },
        {
            name: <span className="text-muted small text-uppercase fw-bold text-center w-100">Status</span>,
            sortable: true,
            sortField: "status",
            center: true,
            cell: (row: BookingData) => getStatusBadge(row.status),
        },
        {
            name: <span className="text-muted small text-uppercase fw-bold text-center w-100">Actions</span>,
            width: "180px",
            center: true,
            cell: (row: BookingData) => (
                <div className="d-flex gap-2 p-1">
                    <Button size="sm" color="primary" onClick={() => handleView(row)} title="View Detailed Summary" className="btn-pill px-3 shadow-sm d-flex align-items-center gap-1">
                        <Eye size={14} /> Full Details
                    </Button>
                </div>
            ),
        },
    ];

    const customStyles = {
        headRow: {
            style: {
                backgroundColor: '#e0f2f1',
                color: '#00695c',
                minHeight: '50px',
                borderBottom: '1px solid #e0e0e0',
            },
        },
        headCells: {
            style: { fontSize: '15px', fontWeight: '600', paddingLeft: '16px', paddingRight: '16px' },
        },
        rows: {
            style: { minHeight: '50px' },
        },
    };

    return (
        <div className='page-body' style={{ backgroundColor: '#f4f7f6', minHeight: '100vh' }}>
            <Breadcrumbs pageTitle="Enterprise Booking Management" parent="Operations" title="Service Bookings" />

            <Container fluid className="px-4">
                <Card className="border-0 shadow-lg" style={{ borderRadius: '15px', overflow: 'hidden' }}>
                    <div style={{
                        background: 'linear-gradient(90deg, #1e3a8a 0%, #3b82f6 100%)',
                        padding: '1.5rem 2rem',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                    }}>
                        <div>
                            <h2 className="text-white mb-0 fw-bold">Live Booking Ledger</h2>
                            <p className="text-white opacity-75 mb-0 small">Monitor and optimize service distribution in real-time</p>
                        </div>
                        <div className="d-flex gap-3">
                        </div>
                    </div>

                    <CardBody className="bg-white ">
                        <Row className="mb-4 align-items-center g-0 border-bottom">
                            <Col md={8}>
                                <div className="d-flex custom-tabs">
                                    {[
                                        { id: '', label: 'All Bookings' },
                                        { id: 'pending', label: 'Pending' },
                                        { id: 'accepted', label: 'Accepted' },
                                        { id: 'active', label: 'Active' },
                                        { id: 'completed', label: 'Completed' },
                                        { id: 'cancelled', label: 'Cancelled' },
                                        { id: 'expired', label: 'Expired' },
                                        { id: 'rejected', label: 'Rejected' },
                                    ].map((status) => (
                                        <div
                                            key={status.id}
                                            className={`tab-item ${selectedStatus === status.id ? 'active-tab' : ''}`}
                                            onClick={() => setSelectedStatus(status.id)}
                                        >
                                            {status.label}
                                        </div>
                                    ))}
                                </div>
                            </Col>
                            <Col md={4} className="ps-md-3 mb-1 mt-3 mt-md-0">
                                <div className="d-flex gap-2 align-items-center">
                                    <div className="search-container flex-grow-1">
                                        <Input
                                            placeholder="Search..."
                                            value={searchTerm}
                                            onChange={(e) => setSearchTerm(e.target.value)}
                                            className="form-control-sm"
                                            style={{ borderRadius: '8px' }}
                                        />
                                    </div>
                                    {(searchTerm || selectedStatus) && (
                                        <Button
                                            size="sm"
                                            color="light"
                                            onClick={() => {
                                                setSearchTerm("");
                                                setSelectedStatus("");
                                            }}
                                            style={{ borderRadius: '8px' }}
                                            title="Clear Filters"
                                        >
                                            <RefreshCcw size={14} />
                                        </Button>
                                    )}
                                </div>
                            </Col>
                        </Row>

                        <div className="rounded border shadow-sm overflow-hidden">
                            <DataTable
                                columns={columns}
                                data={data}
                                progressPending={loading}
                                progressComponent={<div className="d-flex justify-content-center align-items-center w-100" style={{ minHeight: '50vh' }}><Spinner color="primary" /></div>}
                                pagination
                                paginationServer
                                paginationTotalRows={totalRows}
                                onChangeRowsPerPage={handlePerRowsChange}
                                onChangePage={handlePageChange}
                                sortServer
                                onSort={handleSort}
                                highlightOnHover
                                customStyles={customStyles}
                                noDataComponent={
                                    <div className="p-5 text-center">
                                        <div className="mb-3 text-muted opacity-25">
                                            <Info size={64} />
                                        </div>
                                        <h4 className="fw-bold text-dark">No Bookings Found</h4>
                                        <p className="text-muted">There are no service bookings matching your current filters.</p>
                                    </div>
                                }
                            />
                        </div>
                    </CardBody>
                </Card>
            </Container>

            {/* Quick View Modal */}
            <Modal isOpen={viewModal} toggle={() => setViewModal(!viewModal)} size="lg" centered className="modal-premium">
                {selectedBooking && (
                    <>
                        {/* Premium Header */}
                        <div style={{
                            background: '#1e3a8a',
                            padding: '1.25rem 2.5rem',
                            position: 'relative',
                            color: 'white',
                            margin: '0',
                            width: '100%'
                        }}>
                            <button
                                className="btn-close btn-close-white"
                                type="button"
                                onClick={() => setViewModal(false)}
                                style={{ position: 'absolute', top: '1rem', right: '1.5rem', opacity: 0.8 }}
                            />
                            <div className="d-flex align-items-center gap-3">
                                <div className="avatar-container shadow d-flex align-items-center justify-content-center bg-white rounded-circle" style={{ width: '52px', height: '52px', minWidth: '52px' }}>
                                    <div className="rounded-circle d-flex align-items-center justify-content-center" style={{ width: '46px', height: '46px', background: '#f8fafc', border: '1.5px solid #e2e8f0' }}>
                                        <User size={24} className="text-primary" style={{ opacity: 0.8 }} />
                                    </div>
                                </div>
                                <div className="flex-grow-1">
                                    <h3 className="mb-0 fw-bold" style={{ fontSize: '20px', letterSpacing: '-0.3px' }}>{selectedBooking.patient_name}</h3>
                                    <div className="d-flex flex-wrap gap-2 opacity-90 mt-1 align-items-center fw-medium" style={{ fontSize: '13px' }}>
                                        <span><Phone size={13} className="me-1" /> {selectedBooking.patient_mobile}</span>
                                        {selectedBooking.patient_age && <span>• {selectedBooking.patient_age} Years</span>}
                                        {selectedBooking.patient_dob && (
                                            <span>• DOB: {new Date(selectedBooking.patient_dob).toLocaleDateString('en-GB')}</span>
                                        )}
                                        {selectedBooking.review && (
                                            <Badge color="warning" className="text-dark d-flex align-items-center gap-1 ms-1" style={{ borderRadius: '4px', fontSize: '11px' }}>
                                                <Star size={11} fill="currentColor" /> {selectedBooking.review.rating}.0
                                            </Badge>
                                        )}
                                        <div className="ms-1">{getStatusBadge(selectedBooking.status)}</div>
                                        {!!(selectedBooking.is_modified && selectedBooking.modification_reason) && (
                                            <Badge color="warning" className="text-dark ms-1 text-uppercase" style={{ fontSize: '10px', fontWeight: '800', borderRadius: '4px' }}>MODIFIED</Badge>
                                        )}
                                        {selectedBooking.original_bystander_id && (
                                            <Badge color="info" className="text-white ms-1 text-uppercase" style={{ fontSize: '10px', fontWeight: '800', borderRadius: '4px' }}>REASSIGNED</Badge>
                                        )}
                                        <Badge color={selectedBooking.booking_type === 'booknow' ? 'danger' : 'success'} pill className="px-2 py-1 text-uppercase ms-1" style={{ fontSize: '10px', fontWeight: '800' }}>
                                            {selectedBooking.booking_type === 'booknow' ? 'Instant Service' : 'Planned Service'}
                                        </Badge>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <ModalBody className="p-0" style={{ backgroundColor: '#ffffff' }}>
                            <div className="bg-light px-4 px-md-5 pt-1 border-bottom d-flex justify-content-center">
                                <Nav tabs className="border-0 gap-3 gap-md-4 d-flex flex-nowrap align-items-center justify-content-center overflow-auto" style={{ scrollbarWidth: 'none' }}>
                                    <NavItem style={{ flexShrink: 0 }}>
                                        <NavLink
                                            className={`detail-tab-link ${activeTab === '1' ? 'active' : ''}`}
                                            onClick={() => toggleTab('1')}
                                            style={{ cursor: 'pointer', border: 'none', padding: '8px 0 10px 0', fontSize: '13px', fontWeight: '700', color: activeTab === '1' ? '#1e3a8a' : '#64748b', position: 'relative', whiteSpace: 'nowrap' }}
                                        >
                                            <Info size={14} className="me-1" /> Service Overview
                                            {activeTab === '1' && <div style={{ position: 'absolute', bottom: 0, left: 0, width: '100%', height: '3px', background: '#3b82f6', borderRadius: '10px 10px 0 0' }} />}
                                        </NavLink>
                                    </NavItem>
                                    <NavItem style={{ flexShrink: 0 }}>
                                        <NavLink
                                            className={`detail-tab-link ${activeTab === '2' ? 'active' : ''}`}
                                            onClick={() => toggleTab('2')}
                                            style={{ cursor: 'pointer', border: 'none', padding: '8px 0 10px 0', fontSize: '13px', fontWeight: '700', color: activeTab === '2' ? '#1e3a8a' : '#64748b', position: 'relative', whiteSpace: 'nowrap' }}
                                        >
                                            <MapPin size={14} className="me-1" /> Logistics & Geospatial
                                            {activeTab === '2' && <div style={{ position: 'absolute', bottom: 0, left: 0, width: '100%', height: '3px', background: '#3b82f6', borderRadius: '10px 10px 0 0' }} />}
                                        </NavLink>
                                    </NavItem>
                                    <NavItem style={{ flexShrink: 0 }}>
                                        <NavLink
                                            className={`detail-tab-link ${activeTab === '3' ? 'active' : ''}`}
                                            onClick={() => toggleTab('3')}
                                            style={{ cursor: 'pointer', border: 'none', padding: '8px 0 10px 0', fontSize: '13px', fontWeight: '700', color: activeTab === '3' ? '#1e3a8a' : '#64748b', position: 'relative', whiteSpace: 'nowrap' }}
                                        >
                                            <RefreshCcw size={14} className="me-1" /> Operational Audit
                                            {activeTab === '3' && <div style={{ position: 'absolute', bottom: 0, left: 0, width: '100%', height: '3px', background: '#3b82f6', borderRadius: '10px 10px 0 0' }} />}
                                        </NavLink>
                                    </NavItem>
                                    <NavItem style={{ flexShrink: 0 }}>
                                        <NavLink
                                            className={`detail-tab-link ${activeTab === '4' ? 'active' : ''}`}
                                            onClick={() => toggleTab('4')}
                                            style={{ cursor: 'pointer', border: 'none', padding: '8px 0 10px 0', fontSize: '13px', fontWeight: '700', color: activeTab === '4' ? '#1e3a8a' : '#64748b', position: 'relative', whiteSpace: 'nowrap' }}
                                        >
                                            <Shield size={14} className="me-1" /> Belongings Safekeeping
                                            {activeTab === '4' && <div style={{ position: 'absolute', bottom: 0, left: 0, width: '100%', height: '3px', background: '#3b82f6', borderRadius: '10px 10px 0 0' }} />}
                                        </NavLink>
                                    </NavItem>
                                </Nav>
                            </div>

                            <TabContent activeTab={activeTab} className="py-3 px-4 px-md-5">
                                <TabPane tabId="1">
                                    <Row className="g-4">
                                        {/* Row 1: Unified Info Strip */}
                                        <Col md={12}>
                                            <div style={{ display: 'flex', background: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px', overflow: 'hidden' }}>
                                                {/* Assigned Assistant */}
                                                <div style={{ flex: 1, padding: '14px 16px', borderRight: '1px solid #f3f4f6', display: 'flex', flexDirection: 'column', gap: '6px', borderLeft: '3px solid #ef4444' }}>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                        <User size={12} style={{ color: '#ef4444' }} />
                                                        <span style={{ fontSize: '9px', fontWeight: 700, letterSpacing: '0.8px', textTransform: 'uppercase', color: '#9ca3af' }}>Assigned Assistant</span>
                                                    </div>
                                                    {selectedBooking.bystander ? (
                                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                                            <div style={{ fontSize: '14px', fontWeight: 700, color: '#111827' }}>{selectedBooking.bystander.name}</div>
                                                            <div style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '4px', padding: '2px 7px', width: 'fit-content' }}>
                                                                <Phone size={9} style={{ color: '#ef4444' }} />
                                                                <span style={{ fontSize: '11px', fontWeight: 600, color: '#374151' }}>{selectedBooking.bystander.phone_number}</span>
                                                            </div>
                                                        </div>
                                                    ) : (
                                                        <span style={{ fontSize: '11px', color: '#9ca3af', fontStyle: 'italic' }}>No provider assigned</span>
                                                    )}
                                                </div>
                                                {/* Divider hint */}
                                                <div style={{ width: '1px', background: '#f3f4f6', flexShrink: 0 }} />
                                                {/* Schedule Details */}
                                                <div style={{ flex: 1, padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                        <Calendar size={12} style={{ color: '#0ea5e9' }} />
                                                        <span style={{ fontSize: '9px', fontWeight: 700, letterSpacing: '0.8px', textTransform: 'uppercase', color: '#9ca3af' }}>Appointment</span>
                                                    </div>
                                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                                        <div style={{ fontSize: '14px', fontWeight: 700, color: '#111827' }}>{new Date(selectedBooking.date).toLocaleDateString('en-GB')}</div>
                                                        <div style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: '4px', padding: '2px 7px', width: 'fit-content' }}>
                                                            <Clock size={9} style={{ color: '#0ea5e9' }} />
                                                            <span style={{ fontSize: '11px', fontWeight: 600, color: '#374151' }}>{selectedBooking.slot_start_time.substring(0, 5)}</span>
                                                            <span style={{ fontSize: '9px', color: '#9ca3af', fontWeight: 600 }}>START</span>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </Col>

                                        {/* Row 2: Session Analytics — Completed only */}
                                        {selectedBooking.status === 'completed' && selectedBooking.actual_start_at && selectedBooking.actual_end_at && (
                                            <Col md={12}>
                                                <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px', overflow: 'hidden' }}>
                                                    {/* Header */}
                                                    <div style={{ padding: '8px 14px', background: '#f9fafb', borderBottom: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                        <Truck size={12} style={{ color: '#10b981' }} />
                                                        <span style={{ fontSize: '9px', fontWeight: 700, letterSpacing: '0.8px', textTransform: 'uppercase', color: '#6b7280' }}>Session Analytics</span>
                                                    </div>
                                                    {/* Three-column stat row */}
                                                    <div style={{ display: 'flex' }}>
                                                        <div style={{ flex: 1, padding: '14px 16px', borderRight: '1px solid #f3f4f6', display: 'flex', flexDirection: 'column', gap: '3px' }}>
                                                            <div style={{ fontSize: '8px', fontWeight: 700, letterSpacing: '0.8px', color: '#9ca3af', textTransform: 'uppercase' }}>Arrived</div>
                                                            <div style={{ fontSize: '18px', fontWeight: 800, color: '#111827', lineHeight: 1 }}>{new Date(selectedBooking.actual_start_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</div>
                                                            <div style={{ fontSize: '9px', color: '#9ca3af' }}>Actual start time</div>
                                                        </div>
                                                        <div style={{ flex: 1, padding: '14px 16px', borderRight: '1px solid #f3f4f6', display: 'flex', flexDirection: 'column', gap: '3px' }}>
                                                            <div style={{ fontSize: '8px', fontWeight: 700, letterSpacing: '0.8px', color: '#9ca3af', textTransform: 'uppercase' }}>Finished</div>
                                                            <div style={{ fontSize: '18px', fontWeight: 800, color: '#111827', lineHeight: 1 }}>{new Date(selectedBooking.actual_end_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</div>
                                                            <div style={{ fontSize: '9px', color: '#9ca3af' }}>Actual end time</div>
                                                        </div>
                                                        <div style={{ flex: 1, padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: '3px', background: '#f0fdf4' }}>
                                                            <div style={{ fontSize: '8px', fontWeight: 700, letterSpacing: '0.8px', color: '#059669', textTransform: 'uppercase' }}>Total Duration</div>
                                                            <div style={{ fontSize: '22px', fontWeight: 800, color: '#059669', lineHeight: 1 }}>
                                                                {Math.round((new Date(selectedBooking.actual_end_at).getTime() - new Date(selectedBooking.actual_start_at).getTime()) / 60000)}
                                                                <span style={{ fontSize: '11px', marginLeft: '3px', fontWeight: 600, opacity: 0.7 }}>min</span>
                                                            </div>
                                                            <div style={{ fontSize: '9px', color: '#10b981' }}>Service hours logged</div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </Col>
                                        )}

                                        <Col md={12}>
                                            <div className="detail-group mt-2">
                                                <label className="text-muted small fw-bold text-uppercase d-flex align-items-center gap-1 mb-2">
                                                    <Truck size={16} className="text-primary" /> Service Configuration
                                                </label>
                                                <div className="p-4 bg-light rounded border border-dashed d-flex justify-content-between align-items-center" style={{ borderRadius: '12px' }}>
                                                    <div>
                                                        <div className="fw-bold text-primary text-uppercase small mb-1">{selectedBooking.service_location} Assistance</div>
                                                        <div className="text-dark fw-bold" style={{ fontSize: '15px' }}>{selectedBooking.address}</div>
                                                    </div>
                                                    <div className="text-end">
                                                        <Badge color={selectedBooking.booking_type === 'booknow' ? 'danger' : 'success'} className="px-3 py-2 mb-1">
                                                            {selectedBooking.booking_type === 'booknow' ? 'INSTANT' : 'PLANNED'}
                                                        </Badge>
                                                        <div className="small text-muted fw-bold">
                                                            {config.currency}{selectedBooking.total_amount || '0.00'} - {selectedBooking.payment_status === 'paid' ? `PAID BY USER (${selectedBooking.patient_name || 'PATIENT'})` : (selectedBooking.payment_status?.toUpperCase() || 'UNPAID')}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </Col>
                                        {selectedBooking.review && (
                                            <Col md={12}>
                                                <div className="detail-group mt-2">
                                                    <label className="text-muted small fw-bold text-uppercase d-flex align-items-center gap-1 mb-2">
                                                        <Star size={16} className="text-warning fill-warning" fill="currentColor" /> Patient Rating
                                                    </label>
                                                    <div className="p-3 rounded-lg bg-light border border-warning border-opacity-25 shadow-sm">
                                                        <div className="d-flex align-items-center gap-2 mb-1">
                                                            {[1, 2, 3, 4, 5].map((star) => (
                                                                <Star key={star} size={14} className={star <= selectedBooking.review!.rating ? "text-warning fill-warning" : "text-muted"} fill={star <= selectedBooking.review!.rating ? "#ffc107" : "none"} />
                                                            ))}
                                                            <span className="ms-2 fw-bold text-dark">{selectedBooking.review.rating}/5</span>
                                                        </div>
                                                        <p className="mb-0 text-muted small font-italic">"{selectedBooking.review.comment || 'No comment provided.'}"</p>
                                                    </div>
                                                </div>
                                            </Col>
                                        )}
                                    </Row>
                                </TabPane>

                                <TabPane tabId="2">
                                    <Row className="g-3">
                                        <Col md={12}>
                                            <div className="detail-group">
                                                <label className="text-muted small fw-bold text-uppercase d-flex align-items-center gap-1 mb-2" style={{ fontSize: '11px' }}>
                                                    <MapPin size={14} className="text-primary" /> Geospatial Tracking
                                                </label>
                                                <div className="bg-white p-3 rounded border shadow-sm position-relative">
                                                    {(selectedBooking.latitude && selectedBooking.longitude) ? (
                                                        <LocationHistoryMap
                                                            bookingId={selectedBooking.id}
                                                            bookingStatus={selectedBooking.status}
                                                            defaultLat={selectedBooking.latitude}
                                                            defaultLng={selectedBooking.longitude}
                                                            mapHeight="460px"
                                                        />
                                                    ) : (
                                                        <div className="text-muted italic py-3 text-center">Location coordinates not recorded for this request.</div>
                                                    )}
                                                </div>
                                            </div>
                                        </Col>
                                        <Col md={6}>
                                            <div className="detail-group">
                                                <label className="text-muted small fw-bold text-uppercase d-flex align-items-center gap-1 mb-2">
                                                    <XCircle size={16} className="text-info" /> Verification Codes
                                                </label>
                                                <div className="d-flex gap-3">
                                                    <div className="text-center p-3 rounded-lg border w-100 shadow-sm" style={{ backgroundColor: '#fdfdfd', border: '1.5px solid #10b981' }}>
                                                        <div className="small text-muted fw-bold mb-1">START CODE</div>
                                                        <div className="fw-bold text-success fs-5">{selectedBooking.start_code || '----'}</div>
                                                    </div>
                                                    <div className="text-center p-3 rounded-lg border w-100 shadow-sm" style={{ backgroundColor: '#fdfdfd', border: '1.5px solid #ef4444' }}>
                                                        <div className="small text-muted fw-bold mb-1">END CODE</div>
                                                        <div className="fw-bold text-danger fs-5">{selectedBooking.end_code || '----'}</div>
                                                    </div>
                                                </div>
                                            </div>
                                        </Col>
                                        <Col md={6}>
                                            <div className="detail-group">
                                                <label className="text-muted small fw-bold text-uppercase d-flex align-items-center gap-1 mb-2">
                                                    <Clock size={16} className="text-info" /> Duration Monitor
                                                </label>
                                                <div className="p-3 rounded-lg border-2 shadow-sm d-flex flex-column gap-2" style={{ backgroundColor: '#fdfdfd', border: '1.5px solid #64748b', borderRadius: '10px' }}>
                                                    <div className="d-flex justify-content-between align-items-center small">
                                                        <span className="fw-bold text-muted">ACTUAL START:</span>
                                                        <span className="text-dark fw-bold">{selectedBooking.actual_start_at ? new Date(selectedBooking.actual_start_at).toLocaleTimeString() : 'N/A'}</span>
                                                    </div>
                                                    <div className="d-flex justify-content-between align-items-center small">
                                                        <span className="fw-bold text-muted">ACTUAL FINISH:</span>
                                                        <span className="text-dark fw-bold">{selectedBooking.actual_end_at ? new Date(selectedBooking.actual_end_at).toLocaleTimeString() : 'N/A'}</span>
                                                    </div>
                                                </div>
                                            </div>
                                        </Col>
                                    </Row>
                                </TabPane>

                                <TabPane tabId="3">
                                    <div className="detail-group">
                                        <label className="text-muted small fw-bold text-uppercase d-flex align-items-center gap-1 mb-3">
                                            System Foresnsic Engine (Live Audit Trail)
                                        </label>

                                        {/* Operational Alerts Section */}
                                        {!!((selectedBooking.is_modified && selectedBooking.modification_reason) || selectedBooking.status === 'reassigning' || selectedBooking.status === 'rejected' || selectedBooking.status === 'cancelled') && (
                                            <div className="mb-4 rounded-lg overflow-hidden border shadow-sm">
                                                {!!(selectedBooking.is_modified && selectedBooking.modification_reason) && (
                                                    <div className="p-3 bg-white border-bottom small">
                                                        <div className="d-flex justify-content-between">
                                                            <Badge color="warning" className="text-dark mb-1">CONFIGURATION UPDATED</Badge>
                                                            <span className="text-muted">{selectedBooking.modified_at ? new Date(selectedBooking.modified_at).toLocaleString() : ''}</span>
                                                        </div>
                                                        <p className="mb-0 fw-bold">Modification Context: <span className="text-primary fw-medium">{selectedBooking.modification_reason || 'N/A'}</span></p>
                                                    </div>
                                                )}
                                                {selectedBooking.status === 'reassigning' && (
                                                    <div className="p-3 bg-danger bg-opacity-10 small">
                                                        <div className="d-flex justify-content-between mb-2">
                                                            <Badge color="danger">URGENT SWAP MONITOR</Badge>
                                                            <span className="text-danger fw-bold">ATTEMPT #{selectedBooking.reassign_attempt_count || 1}</span>
                                                        </div>
                                                        <p className="mb-0 text-dark">Pooled {Array.isArray(selectedBooking.notified_bystander_ids) ? selectedBooking.notified_bystander_ids.length : 0} notifications for swap reason: <span className="fw-bold">"{selectedBooking.reassign_reason}"</span></p>
                                                    </div>
                                                )}
                                                {(selectedBooking.status === 'rejected' || selectedBooking.status === 'cancelled') && (
                                                    <div className="p-3 bg-white border-top small" style={{ borderLeft: `4px solid ${selectedBooking.status === 'cancelled' ? '#6c757d' : '#dc3545'}` }}>
                                                        <div className="d-flex justify-content-between mb-1">
                                                            <Badge color={selectedBooking.status === 'cancelled' ? 'secondary' : 'danger'}>
                                                                {selectedBooking.status === 'cancelled' ? 'USER CANCELLATION' : 'BYSTANDER REJECTION'}
                                                            </Badge>
                                                            <span className="text-muted fw-bold">TERMINATION LOG</span>
                                                        </div>
                                                        <p className="mb-0 text-dark fw-bold" style={{ fontSize: '13px' }}>Reason: <span className="fw-medium text-muted">{selectedBooking.rejection_detail || (selectedBooking.status === 'cancelled' ? 'Cancelled by patient.' : 'Bystander declined the request.')}</span></p>
                                                    </div>
                                                )}
                                            </div>
                                        )}

                                        <div className="audit-timeline ps-2">
                                            {/* Initial Creation */}
                                            <div className="audit-item d-flex gap-3 mb-4">
                                                <div className="audit-icon-wrapper pt-1">
                                                    <div className="audit-dot bg-success shadow-sm"></div>
                                                    <div className="audit-line"></div>
                                                </div>
                                                <div className="audit-content">
                                                    <div className="d-flex justify-content-between">
                                                        <span className="fw-bold text-dark small">Booking Initialized</span>
                                                        <small className="text-muted">{new Date(selectedBooking.created_at).toLocaleString([], { dateStyle: 'short', timeStyle: 'short' })}</small>
                                                    </div>
                                                    <p className="mb-0 text-muted" style={{ fontSize: '13px' }}>Request submitted successfully for patient {selectedBooking.patient_name}.</p>
                                                </div>
                                            </div>

                                            {/* Swap Log (Only if happened) */}
                                            {!!(selectedBooking.original_bystander_id || selectedBooking.status === 'reassigning') && (
                                                <div className="audit-item d-flex gap-3 mb-4">
                                                    <div className="audit-icon-wrapper pt-1">
                                                        <div className="audit-dot bg-danger pulse-red"></div>
                                                        <div className="audit-line"></div>
                                                    </div>
                                                    <div className="audit-content">
                                                        <div className="d-flex justify-content-between">
                                                            <span className="fw-bold text-dark small">Automated Reassignment Triggered</span>
                                                            <small className="text-muted">{selectedBooking.last_reassign_attempt_at ? new Date(selectedBooking.last_reassign_attempt_at).toLocaleString([], { dateStyle: 'short', timeStyle: 'short' }) : 'In Progress'}</small>
                                                        </div>
                                                        <div className="mb-0 text-muted" style={{ fontSize: '13px' }}>
                                                            {selectedBooking.original_bystander ? (
                                                                <><span className="text-danger fw-bold">{selectedBooking.original_bystander.name}</span> initialized a shift swap request.</>
                                                            ) : (
                                                                <>System triggered an automated search for a replacement assistant.</>
                                                            )}
                                                            <div className="mt-2 p-2 bg-light rounded border-start border-primary border-3">
                                                                <div className="d-flex align-items-center gap-1 mb-1 fw-bold text-dark" style={{ fontSize: '11px' }}>
                                                                    <Info size={12} className="text-primary" /> NOTIFICATION RECIPIENTS:
                                                                </div>
                                                                <div className="small text-muted fw-medium" style={{ lineHeight: '1.4' }}>
                                                                    {(() => {
                                                                        const rawIds = selectedBooking.notified_bystander_ids;
                                                                        const notifiedIds = Array.isArray(rawIds) ? rawIds : [];

                                                                        if (notifiedIds.length === 0) return 'No specific bystanders notified.';
                                                                        return notifiedIds.map((id: number, idx: number) => {
                                                                            const b = bystanders.find(item => item.id === id);
                                                                            return (
                                                                                <React.Fragment key={id}>
                                                                                    <span className="text-dark">{b ? b.name : `Bystander #${id}`}</span>
                                                                                    {idx < notifiedIds.length - 1 ? ', ' : ''}
                                                                                </React.Fragment>
                                                                            );
                                                                        });
                                                                    })()}
                                                                </div>
                                                                {selectedBooking.reassign_reason && (
                                                                    <div className="mt-2 text-dark small fw-bold" style={{ fontSize: '12px' }}>
                                                                        REASON: <span className="fw-normal text-muted italic">"{selectedBooking.reassign_reason}"</span>
                                                                    </div>
                                                                )}
                                                                {selectedBooking.bystander_id && selectedBooking.original_bystander_id && (
                                                                    <div className="mt-2 pt-2 border-top small">
                                                                        <span className="fw-bold text-success">ACCEPTED BY: </span>
                                                                        <span className="text-dark fw-bold">{selectedBooking.bystander?.name || `#${selectedBooking.bystander_id}`}</span>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}

                                            {/* Outcome Log */}
                                            {!!selectedBooking.bystander && (
                                                <div className="audit-item d-flex gap-3 mb-0">
                                                    <div className="audit-icon-wrapper pt-1">
                                                        <div className={`audit-dot ${['expired', 'rejected', 'cancelled'].includes(selectedBooking.status) ? 'bg-secondary' : selectedBooking.status === 'completed' ? 'bg-success' : 'bg-primary'}`}></div>
                                                    </div>
                                                    <div className="audit-content">
                                                        <div className="d-flex justify-content-between">
                                                            <span className="fw-bold text-dark small">
                                                                {selectedBooking.status === 'pending' ? 'Pending Claim' :
                                                                    selectedBooking.status === 'expired' ? 'Assignment Expired' :
                                                                        selectedBooking.status === 'rejected' ? 'Bystander Rejected' :
                                                                            selectedBooking.status === 'cancelled' ? 'User Cancelled' :
                                                                                selectedBooking.status === 'completed' ? 'Service Completed' :
                                                                                    'Assignment Secured'}
                                                            </span>
                                                        </div>
                                                        <div className="mb-0 text-muted" style={{ fontSize: '13px' }}>
                                                            {selectedBooking.status === 'completed' ? (
                                                                <>
                                                                    <div className="text-dark fw-medium mb-1">
                                                                        {selectedBooking.original_bystander_id ? 'Swap secured: ' : ''}
                                                                        Service successfully completed by <span className="text-primary fw-bold">{selectedBooking.bystander.name}</span>.
                                                                    </div>
                                                                    {selectedBooking.original_bystander_id && (
                                                                        <div className="text-success small fw-bold d-flex align-items-center gap-1 mb-1">
                                                                            <CheckCircle size={12} /> REASSIGNMENT SUCCESSFUL
                                                                        </div>
                                                                    )}
                                                                    {selectedBooking.review && (
                                                                        <div className="bg-light p-2 rounded border mt-1">
                                                                            <div className="d-flex align-items-center gap-1 mb-1">
                                                                                <Star size={10} fill="#ffc107" className="text-warning" />
                                                                                <span className="fw-bold text-dark" style={{ fontSize: '11px' }}>{selectedBooking.review.rating}/5 Rating Received</span>
                                                                            </div>
                                                                            <div className="small italic text-muted">"{selectedBooking.review.comment || 'No specific comment provided.'}"</div>
                                                                        </div>
                                                                    )}
                                                                </>
                                                            ) : ['accepted', 'active'].includes(selectedBooking.status) ? (
                                                                <>
                                                                    <div className="text-dark fw-medium mb-1">
                                                                        {selectedBooking.original_bystander_id ? 'Swap secured: ' : ''}
                                                                        Assistant <span className="text-primary fw-bold">{selectedBooking.bystander.name}</span> is officially managing this shift.
                                                                    </div>
                                                                    {selectedBooking.original_bystander_id && (
                                                                        <div className="text-success small fw-bold d-flex align-items-center gap-1">
                                                                            <CheckCircle size={12} /> REASSIGNMENT SUCCESSFUL
                                                                        </div>
                                                                    )}
                                                                </>
                                                            ) : (
                                                                <>Shift was assigned to <span className="text-muted fw-bold">{selectedBooking.bystander.name}</span> but {selectedBooking.status === 'rejected' ? 'they declined the request.' : selectedBooking.status === 'cancelled' ? 'the user cancelled the request.' : 'not finalized.'}</>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </TabPane>

                                <TabPane tabId="4">
                                    {loadingBelongings ? (
                                        <div className="text-center py-5">
                                            <Spinner color="primary" />
                                            <div className="text-muted mt-2">Loading safekeeping audit records...</div>
                                        </div>
                                    ) : belongings.length === 0 ? (
                                        <div className="text-center py-5 border rounded bg-light">
                                            <Shield size={40} className="text-muted mb-2" />
                                            <div className="fw-bold text-dark">No Belongings in Safekeeping</div>
                                            <div className="text-muted small">No personal belongings custody handover was recorded for this booking.</div>
                                        </div>
                                    ) : (
                                        <div className="d-flex flex-column gap-3">
                                            {belongings.map((b: any) => (
                                                <div key={b.id} className="border rounded-3 p-3 bg-white shadow-sm">
                                                    <div className="d-flex justify-content-between align-items-center mb-3 pb-2 border-bottom">
                                                        <div className="d-flex align-items-center gap-2">
                                                            <Shield size={18} className="text-primary" />
                                                            <span className="fw-bold text-dark">Custody Record #{b.id}</span>
                                                            <span className="text-muted small">• Code: {b.handover_code}</span>
                                                        </div>
                                                        <Badge color={
                                                            b.status === 'in_custody' ? 'primary' :
                                                                b.status === 'returned' ? 'success' :
                                                                    b.status === 'pending_return' ? 'info' :
                                                                        b.status === 'rejected' ? 'danger' : 'warning'
                                                        } className="px-3 py-2 text-uppercase">
                                                            {b.status.replace('_', ' ')}
                                                        </Badge>
                                                    </div>

                                                    <Row className="g-3">
                                                        <Col md={b.photo_url ? 8 : 12}>
                                                            <div className="mb-2">
                                                                <Label className="text-muted small fw-bold text-uppercase d-block mb-1">Items in Custody</Label>
                                                                <div className="d-flex flex-wrap gap-2">
                                                                    {(Array.isArray(b.items) ? b.items : typeof b.items === 'string' ? JSON.parse(b.items || '[]') : []).map((item: string, idx: number) => (
                                                                        <Badge key={idx} color="light" className="text-dark border px-2 py-1">
                                                                            {item.replace('_', ' ').toUpperCase()}
                                                                        </Badge>
                                                                    ))}
                                                                </div>
                                                            </div>

                                                            {b.notes && (
                                                                <div className="mb-2">
                                                                    <Label className="text-muted small fw-bold text-uppercase d-block mb-1">Companion Notes</Label>
                                                                    <div className="p-2 bg-light rounded text-dark small">{b.notes}</div>
                                                                </div>
                                                            )}

                                                            <div className="mt-3 pt-2 border-top">
                                                                <Row className="small text-muted">
                                                                    <Col sm={4}>
                                                                        <span className="fw-bold text-dark d-block">Initiated:</span>
                                                                        {b.created_at ? new Date(b.created_at).toLocaleString() : 'N/A'}
                                                                    </Col>
                                                                    <Col sm={4}>
                                                                        <span className="fw-bold text-dark d-block">Authorized:</span>
                                                                        {b.authorized_at ? new Date(b.authorized_at).toLocaleString() : 'Pending'}
                                                                    </Col>
                                                                    <Col sm={4}>
                                                                        <span className="fw-bold text-dark d-block">Returned:</span>
                                                                        {b.returned_at ? new Date(b.returned_at).toLocaleString() : (b.status === 'in_custody' ? 'In Custody' : 'N/A')}
                                                                    </Col>
                                                                </Row>
                                                            </div>
                                                        </Col>

                                                        {b.photo_url && (
                                                            <Col md={4}>
                                                                <Label className="text-muted small fw-bold text-uppercase d-block mb-1">Photo Evidence</Label>
                                                                <a href={getBelongingPhotoUrl(b)} target="_blank" rel="noopener noreferrer">
                                                                    <img
                                                                        src={getBelongingPhotoUrl(b)}
                                                                        alt="Belongings Proof"
                                                                        className="img-fluid rounded border shadow-sm"
                                                                        style={{ maxHeight: '180px', objectFit: 'cover', width: '100%', cursor: 'pointer' }}
                                                                        onError={(e) => {
                                                                            const target = e.currentTarget;
                                                                            if (!target.dataset.tried) {
                                                                                target.dataset.tried = 'true';
                                                                                target.src = `http://192.168.1.29:8000/storage/uploads/${b.photo_url?.replace(/^uploads\//, '')}`;
                                                                            }
                                                                        }}
                                                                    />
                                                                </a>
                                                                <div className="text-muted small text-center mt-1">Click to view full photo</div>
                                                            </Col>
                                                        )}
                                                    </Row>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </TabPane>
                            </TabContent>
                        </ModalBody>

                    </>
                )}
            </Modal>

            {/* Management/Edit Modal */}
            <Modal isOpen={editModal} toggle={() => setEditModal(!editModal)} size="md" centered>
                <ModalHeader toggle={() => setEditModal(!editModal)} className="bg-primary text-white border-0 py-3">
                    <div className="d-flex align-items-center gap-2">
                        <Edit size={20} />
                        <span>Manage Booking #{selectedBooking?.id}</span>
                    </div>
                </ModalHeader>
                <ModalBody className="p-4">
                    <FormGroup>
                        <Label className="fw-bold small text-muted text-uppercase">Assign Service Provider</Label>
                        <Input
                            type="select"
                            className="form-control-lg border-2"
                            value={editBystanderId}
                            onChange={(e) => setEditBystanderId(e.target.value)}
                        >
                            <option value="">-- No Provider Assigned --</option>
                            {bystanders.map(b => (
                                <option key={b.id} value={b.id}>{b.name} ({b.phone_number})</option>
                            ))}
                        </Input>
                        <small className="text-muted mt-1 d-block">Choose a qualified bystander for this patient request.</small>
                    </FormGroup>

                    <FormGroup className="mt-4">
                        <Label className="fw-bold small text-muted text-uppercase">Execution Status</Label>
                        <Input
                            type="select"
                            className="form-control-lg border-2"
                            value={editStatus}
                            onChange={(e) => setEditStatus(e.target.value)}
                        >
                            <option value="pending">Pending Receipt</option>
                            <option value="accepted">Accepted / Preparation</option>
                            <option value="active">Active On-Site</option>
                            <option value="completed">Service Successfully Completed</option>
                            <option value="cancelled">Cancelled by Authority</option>
                            <option value="rejected">Rejected by Operator</option>
                            <option value="expired">Request Period Expired</option>
                        </Input>
                    </FormGroup>

                    <div className="mt-4 p-3 bg-light rounded d-flex align-items-center gap-3 border shadow-inset">
                        <div className="bg-warning p-2 rounded text-white shadow-sm">
                            <Info size={18} />
                        </div>
                        <small className="text-dark fw-medium lh-sm">
                            Modifying the status or provider will trigger automated push notifications to both the patient and the assigned bystander.
                        </small>
                    </div>
                </ModalBody>
                <ModalFooter className="bg-light border-0 p-4 pt-0">
                    <Button color="outline-secondary" className="btn-pill px-4" onClick={() => setEditModal(false)}>Discard</Button>
                    <Button color="primary" className="btn-pill px-4 shadow-primary d-flex align-items-center gap-2" onClick={handleUpdateBooking} disabled={updateLoading}>
                        {updateLoading ? <Spinner size="sm" /> : <Save size={16} />}
                        <span>Publish Changes</span>
                    </Button>
                </ModalFooter>
            </Modal>

            <ToastContainer />

            <style>{`
                .btn-pill {
                    border-radius: 50px !important;
                }
                .shadow-primary {
                    box-shadow: 0 4px 14px 0 rgba(59, 130, 246, 0.39) !important;
                }
                .modal-premium .modal-content {
                    border-radius: 20px !important;
                    overflow: hidden !important;
                    border: none !important;
                    box-shadow: 0 25px 50px -12px rgb(0 0 0 / 0.25) !important;
                }
                .modal-premium .modal-body {
                    background-color: #ffffff !important;
                }
                .form-control-lg {
                    border-radius: 12px !important;
                }
                .form-control:focus {
                    box-shadow: 0 0 0 0.25rem rgba(30, 58, 138, 0.1) !important;
                    border-color: #1e3a8a !important;
                }
                .shadow-inset {
                    box-shadow: inset 0 2px 4px 0 rgba(0, 0, 0, 0.05);
                }
                .custom-tabs {
                    gap: 30px;
                }
                .tab-item {
                    font-size: 14px;
                    font-weight: 600;
                    color: #64748b;
                    cursor: pointer;
                    padding: 12px 2px;
                    transition: all 0.3s ease;
                    position: relative;
                    white-space: nowrap;
                }
                .tab-item:hover {
                    color: #1e3a8a;
                }
                .active-tab {
                    color: #1e3a8a;
                }
                .active-tab::after {
                    content: '';
                    position: absolute;
                    bottom: -1px;
                    left: 0;
                    width: 100%;
                    height: 3px;
                    background-color: #3b82f6;
                    border-radius: 10px 10px 0 0;
                }
                .search-container .form-control {
                    transition: all 0.2s;
                }
                .search-container .form-control:focus {
                    background-color: #fff !important;
                    box-shadow: 0 4px 6px -1px rgb(0 0 0 / 0.1) !important;
                }
                @keyframes pulse {
                    0%, 100% { opacity: 1; }
                    50% { opacity: .5; }
                }
                .audit-timeline {
                    padding-left: 10px;
                }
                .audit-icon-wrapper {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    width: 20px;
                }
                .audit-dot {
                    width: 12px;
                    height: 12px;
                    border-radius: 50%;
                    flex-shrink: 0;
                    border: 2px solid #fff;
                    z-index: 2;
                }
                .audit-line {
                    width: 2px;
                    background-color: #e2e8f0;
                    flex-grow: 1;
                    margin: 4px 0;
                }
                .audit-content {
                    width: 100%;
                }
                .pulse-red {
                    animation: pulse-red-animation 2s infinite;
                }
                @keyframes pulse-red-animation {
                    0% { box-shadow: 0 0 0 0 rgba(239, 68, 68, 0.4); }
                    70% { box-shadow: 0 0 0 10px rgba(239, 68, 68, 0); }
                    100% { box-shadow: 0 0 0 0 rgba(239, 68, 68, 0); }
                }
            `}</style>
        </div>
    );
}
